import {useDispatch} from 'react-redux'
import {DownloadStatus, ORDER_ACTION_VIEW_MODE, OrderActionType, OrderStatus} from '../constants'
import {OrderModel} from '../models/OrderModel'
import {
  getCurrentDownloadOrderCredit,
  removeAsyncWorkingOrders,
  retryOrder,
  selectSelectedItems,
  selectSelectedMode,
  selectWorkingOrders,
} from '../redux/OrderRedux'
import {KTSVG} from '../../../../_metronic/helpers'
import {FC, useEffect, useRef} from 'react'
import {Spinner} from 'react-bootstrap-v5'
import {DatabaseReference, off, onValue, ref} from 'firebase/database'
import {useAppSelector} from '../../../../setup/redux/hooks'
import {database} from '../../../../setup/firebase'
import {toast} from 'react-toastify'
import {OrderActionModel} from '../models/OrderActionModel'
import Tooltip from 'react-tooltip-lite'
import {useIntl} from 'react-intl'

type Props = {
  order: OrderModel
  viewMode: ORDER_ACTION_VIEW_MODE
}

const OrderActionBox: FC<Props> = (props) => {
  const dispatch = useDispatch()
  const intl = useIntl()
  const order = props.order
  const status = props.order.status
  const isFullMode = props.viewMode === ORDER_ACTION_VIEW_MODE.FULL
  const workingOrders = useAppSelector(selectWorkingOrders)
  const selectedItems = useAppSelector(selectSelectedItems)

  const selectedMode = useAppSelector(selectSelectedMode)
  const orderListenerRef = useRef<DatabaseReference | null>(null)
  let actions: JSX.Element[] = []
  /**
   * handleDownloadAction
   *
   * @param snapshot
   * @param workingJob
   * @returns
   */
  const handleDownloadAction = (snapshot: any, workingJob: OrderActionModel) => {
    const data = snapshot.val()
    if (!data) {
      return
    }
    if (data.status === DownloadStatus.DOWNLOADING) {
      toast.update(workingJob.toast, {
        render: intl.formatMessage({id: data.message}) + '...' + Math.round(data.percentage) + '%',
      })
    } else {
      toast.update(workingJob.toast, {
        render: intl.formatMessage({id: data.message}),
        autoClose: 3000,
      })
      if (data.status === DownloadStatus.DONE) {
        const a = document.createElement('a')
        a.href = data.downloadUrl
        a.download = data.filename
        a.style.display = 'none'
        document.body.appendChild(a)
        a.click()
        orderListenerRef.current && off(orderListenerRef.current, 'value')
        orderListenerRef.current = null
        document.body.removeChild(a)
        dispatch(removeAsyncWorkingOrders({orderId: data.orderId}))
      }
    }
  }
  /**
   * handleRetryAction
   *
   * @param snapshot
   */
  const handleRetryAction = (snapshot: any) => {
    const order = snapshot.val()
    if (order && order.status === OrderStatus.COMPLETED) {
      orderListenerRef.current && off(orderListenerRef.current, 'value')
      orderListenerRef.current = null
      dispatch(removeAsyncWorkingOrders({orderId: order.id}))
    }
    if (order && order.status === OrderStatus.AI_FAILED) {
      orderListenerRef.current && off(orderListenerRef.current, 'value')
      orderListenerRef.current = null
      dispatch(removeAsyncWorkingOrders({orderId: order.id}))
    }
  }

  const workingJob = workingOrders.find((item) => order.id === item.orderId)

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!workingJob) {
      if (orderListenerRef.current) {
        off(orderListenerRef.current)
        orderListenerRef.current = null
      }
      return
    }

    const listenerRef = ref(database, workingJob.asyncListenerPath)
    orderListenerRef.current = listenerRef

    switch (workingJob.type) {
      case OrderActionType.Download:
        onValue(listenerRef, (snapshot) => handleDownloadAction(snapshot, workingJob))
        break
      case OrderActionType.Retry:
        onValue(listenerRef, handleRetryAction)
        break
    }

    return () => {
      off(listenerRef)
      orderListenerRef.current = null
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workingJob, intl])
  if ([OrderStatus.PENDING, OrderStatus.COMPLETED, OrderStatus.AI_FAILED].includes(status)) {
    actions.push(
      <Tooltip
        content={
          selectedItems.length > 0
            ? intl.formatMessage({id: 'ORDER.ACTIONS.DOWNLOAD_SELECTED'})
            : intl.formatMessage({id: 'ORDER.ACTIONS.DOWNLOAD'})
        }
        className='tooltips'
      >
        <button
          type='button'
          disabled={workingJob !== undefined || (selectedMode && selectedItems.length === 0)}
          className={
            'btn btn-icon bg-light-success btn-active-color-primary btn-sm me-1 p-1 ' +
            (isFullMode ? 'w-100' : '')
          }
          onClick={() => {
            //re-calculate the order credit
            dispatch(
              getCurrentDownloadOrderCredit({
                orderId: order.id,
              })
            )
          }}
        >
          {workingJob ? (
            <Spinner size='sm' animation='border' />
          ) : (
            <div className='fw-bold text-gray-800 text-hover-primary fs-6'>
              <KTSVG
                path={'/media/icons/duotune/files/fil021.svg'}
                className='svg-icon svg-icon-2x svg-icon-success'
              ></KTSVG>
              {isFullMode &&
                (selectedItems.length > 0
                  ? intl.formatMessage({id: 'ORDER.ACTIONS.DOWNLOAD_SELECTED'})
                  : intl.formatMessage({id: 'ORDER.ACTIONS.DOWNLOAD'}))}
            </div>
          )}
        </button>
      </Tooltip>
    )
  }
  if (status === OrderStatus.AI_FAILED) {
    actions.push(
      <Tooltip content={intl.formatMessage({id: 'ORDER.ACTIONS.RETRY'})} className='tooltips'>
        <button
          type='button'
          disabled={workingJob ? true : false}
          className={
            'btn btn-icon bg-light-warning btn-active-color-primary btn-sm me-1 p-1 ' +
            (isFullMode ? 'w-100' : '')
          }
          onClick={() => {
            dispatch(retryOrder(order.id))
          }}
        >
          {workingJob ? (
            <Spinner size='sm' animation='border' />
          ) : (
            <div className='fw-bold text-gray-800 text-hover-primary fs-6'>
              <KTSVG
                path={'/media/icons/duotune/arrows/arr029.svg'}
                className='svg-icon svg-icon-2x svg-icon-success'
              />
              {isFullMode && intl.formatMessage({id: 'ORDER.ACTIONS.RETRY'})}
            </div>
          )}
        </button>
      </Tooltip>
    )
  }

  return (
    <>
      {actions.map((action, index) => (
        <div className='p-1' key={index}>{action}</div>
      ))}
    </>
  )
}
export default OrderActionBox
